import React, { useState, useEffect, useRef } from "react";
import { useSelector, useDispatch } from "react-redux";
import { useNavigate, useLocation } from "react-router-dom";
import {
  Button,
  Image,
  Row,
  Col,
  Card,
  Dropdown,
  Space,
  theme,
  message,
} from "antd";
import {
  ReadOutlined,
  EllipsisOutlined,
  KeyOutlined,
  LogoutOutlined,
  HistoryOutlined,
  HomeOutlined,
  GiftOutlined,
  ShoppingCartOutlined,
} from "@ant-design/icons";
import logout from "../../actions/logout";
import { axiosClient } from "../../apiClient";
import { useParams } from "react-router-dom";

const { useToken } = theme;

function Navbar({ setAllCampaignsModal, handleToggle }) {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const { token } = useToken();
  const headerRef = useRef(null);

  const { logotipoUrl } = useSelector((state) => state.sellerData);
  const { logged } = useSelector((state) => state.authentication);
  const cart = useSelector((state) => state.cart);
  const productsTotalAfterDiscount = useSelector(
    (state) => state.cart.productsTotalAfterDiscount,
  );
  const minOrderValue = useSelector((state) => state.cart.minOrderValue);

  const [visibleCardsCount, setVisibleCardsCount] = useState(0);
  const [failMessage, setFailMessage] = useState(false);

  const { orderRebuyId } = useParams();

  const isCheckout = location.pathname.includes("checkout");
  const isEditing = !!orderRebuyId;

  useEffect(() => {
    if (productsTotalAfterDiscount == 0) {
      setFailMessage("Adicione produtos no carrinho para finalizar o pedido");
    } else if (minOrderValue > productsTotalAfterDiscount) {
      setFailMessage(
        `Adicione mais R$${parseFloat(
          minOrderValue - productsTotalAfterDiscount,
        ).toFixed(
          2,
        )} no carrinho para atingir o pedido mínimo de R$${parseFloat(
          minOrderValue,
        ).toFixed(2)}`,
      );
    } else {
      setFailMessage(false);
    }
  }, [productsTotalAfterDiscount, minOrderValue]);

  const finalizeOrder = () => {
    if (!orderRebuyId) {
      failMessage
        ? message.warning(failMessage)
        : dispatch({ type: "GO_CHECKOUT" });
    } else {
      axiosClient
        .post(
          "/cart/order/update",
          { orderId: orderRebuyId, cart },
          { withCredentials: true },
        )
        .then((response) => {
          if (response.data.status === "Success") {
            dispatch({ type: "RESET_REBUY" });
            navigate(`/editar/checkout/${orderRebuyId}`);
          }
        });
    }
  };

  const cards = [
    ...(logged === "seller" || logged === "sellerUser"
      ? [
          {
            icon: <HomeOutlined />,
            label: "Painel",
            action: () => navigate("/admin"),
          },
        ]
      : []),
    {
      icon: <GiftOutlined />,
      label: "Campanhas",
      action: () => setAllCampaignsModal(true),
    },
    {
      icon: <HistoryOutlined />,
      label: "Histórico",
      action: () => !orderRebuyId && handleToggle(),
    },
    {
      icon: <ShoppingCartOutlined />,
      label: "Finalizar Pedido",
      action: finalizeOrder,
    },
  ];

  useEffect(() => {
    const resize = () => {
      if (!headerRef.current) return;
      const width =
        headerRef.current.offsetWidth - (logotipoUrl ? 180 : 0) - 60;
      const count = Math.floor(width / 94);
      setVisibleCardsCount(Math.max(0, Math.min(count, cards.length)));
    };
    resize();
    const obs = new ResizeObserver(resize);
    obs.observe(headerRef.current);
    return () => obs.disconnect();
  }, [cards.length, logotipoUrl]);

  const menu = {
    items: [
      ...cards.slice(visibleCardsCount).map((c, i) => ({
        key: i,
        icon: React.cloneElement(c.icon, { style: { fontSize: 12 } }),
        label: c.label,
        onClick: c.action,
      })),
      ...(!isCheckout && !isEditing
        ? [
            ...(cards.slice(visibleCardsCount).length > 0
              ? [{ type: "divider" }]
              : []),
            {
              key: "change-pass",
              icon: <KeyOutlined />,
              label: "Trocar Senha",
              onClick: () => dispatch({ type: "OPEN_CHANGE_PASS" }),
            },
            {
              key: "logout",
              icon: <LogoutOutlined />,
              label: "Sair",
              onClick: logout,
            },
          ]
        : []),
    ],
  };

  const CardButton = ({ icon, label, onClick, width = 90 }) => {
    const [hovered, setHovered] = useState(false);

    const baseStyle = {
      width,
      height: 55,
      borderRadius: 8,
      boxShadow: "none",
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      justifyContent: "center",
      cursor: "pointer",
      transition: "all 0.2s",
      background: hovered ? token.colorPrimary : token.colorBgContainer,
      color: hovered ? token.colorTextLightSolid : token.colorText,
    };

    return (
      <Card
        style={baseStyle}
        onClick={onClick}
        onMouseEnter={() => setHovered(true)}
        onMouseLeave={() => setHovered(false)}
      >
        <Space direction="vertical" size={0} align="center">
          <div style={{ fontSize: 18 }}>
            {React.cloneElement(icon, { style: { fontSize: 18 } })}
          </div>
          <div style={{ fontSize: 12, whiteSpace: "nowrap" }}>{label}</div>
        </Space>
      </Card>
    );
  };

  return (
    <header
      ref={headerRef}
      style={{
        top: logged === "seller" || logged === "sellerUser" ? 50 : 0,
        position: "fixed",
        zIndex: 10,
        width: "100%",
        height: 64,
        backgroundColor: token.colorBgContainer,
        boxShadow: "1px 1px 4px rgba(0,0,0,0.1)",
      }}
    >
      <Row
        justify="space-between"
        align="middle"
        style={{ height: "100%", padding: "0 6px" }}
      >
        <Col>
          {logotipoUrl && (
            <Image
              src={logotipoUrl}
              preview={false}
              height={55}
              style={{ cursor: "pointer", objectFit: "contain", maxWidth: 180 }}
              onClick={() => navigate("/")}
            />
          )}
        </Col>
        <Col>
          <Space size={4}>
            {isCheckout ? (
              <CardButton
                icon={<ReadOutlined />}
                label="Retornar aos Produtos"
                onClick={() =>
                  navigate(orderRebuyId ? `/editar/${orderRebuyId}` : "/")
                }
                width={140}
              />
            ) : isEditing ? (
              <CardButton
                icon={<ShoppingCartOutlined />}
                label="Finalizar Pedido"
                onClick={finalizeOrder}
                width={140}
              />
            ) : (
              <>
                {cards.slice(0, visibleCardsCount).map((c, i) => (
                  <CardButton
                    key={i}
                    icon={c.icon}
                    label={c.label}
                    onClick={c.action}
                  />
                ))}

                <Dropdown menu={menu} trigger={["click"]}>
                  <Button
                    type="text"
                    shape="circle"
                    size="large"
                    icon={<EllipsisOutlined style={{ fontSize: 24 }} />}
                    style={{ padding: 14 }}
                  />
                </Dropdown>
              </>
            )}
          </Space>
        </Col>
      </Row>
    </header>
  );
}

export default Navbar;
